import {
  FirestoreOrganisationMarketplace,
  SeasonalEventRegion,
} from "reedy-data/models";
import { differenceInCalendarDays, parseISO } from "date-fns";
import type {
  SeasonalEvent,
  SeasonalEventOccurrence,
} from "~/integrations/firebase/collections/seasonal-events";
import { displayWindowEnd } from "~/components/seasonal-events/calendar-utils";

const MARKETPLACE_REGIONS: Partial<
  Record<FirestoreOrganisationMarketplace, SeasonalEventRegion>
> = {
  [FirestoreOrganisationMarketplace.US]: SeasonalEventRegion.US,
  [FirestoreOrganisationMarketplace.UK]: SeasonalEventRegion.UK,
  [FirestoreOrganisationMarketplace.GER]: SeasonalEventRegion.DE,
  [FirestoreOrganisationMarketplace.AU]: SeasonalEventRegion.AUS,
};

export const getSeasonalEventRegion = (
  marketplace: FirestoreOrganisationMarketplace | null | undefined,
): SeasonalEventRegion | null =>
  marketplace ? (MARKETPLACE_REGIONS[marketplace] ?? null) : null;

// The pipeline builds event_id as `{slug}-{region}`.
const eventSlug = (event: SeasonalEvent): string => {
  const suffix = `-${event.region}`;
  return event.event_id.endsWith(suffix)
    ? event.event_id.slice(0, -suffix.length)
    : event.event_id;
};

// Same rule as the pipeline's ranges_touch: overlapping or one day apart.
const startsByDayAfterEnd = (
  occurrence: SeasonalEventOccurrence,
  other: SeasonalEventOccurrence,
): boolean =>
  differenceInCalendarDays(parseISO(other.end), parseISO(occurrence.start)) >=
  -1;

const datesTouch = (left: SeasonalEvent, right: SeasonalEvent): boolean =>
  left.occurrences.some((a) =>
    right.occurrences.some(
      (b) => startsByDayAfterEnd(a, b) && startsByDayAfterEnd(b, a),
    ),
  );

// Same slug only when the dates touch: the UK "World Book Day" in March is not
// the April day. The pipeline's match step links with the same rule.
const isSameEvent = (
  homeEvent: SeasonalEvent,
  internationalEvent: SeasonalEvent,
): boolean =>
  (eventSlug(homeEvent) === eventSlug(internationalEvent) &&
    datesTouch(homeEvent, internationalEvent)) ||
  homeEvent.international_match?.event_id === internationalEvent.event_id;

// Every view stops at the display window, so a later date does not count.
const isInWindow = (event: SeasonalEvent, today: string): boolean =>
  event.occurrences.some(
    (occurrence) =>
      occurrence.end >= today && occurrence.start <= displayWindowEnd(today),
  );

// The home copy wins (local title and prompts) unless the International copy
// has something it lacks: a date in the window, or suggested searches. Prompts
// count only when the International copy is in the window too.
const homeCopyLoses = (
  homeEvent: SeasonalEvent,
  internationalEvent: SeasonalEvent,
  today: string,
): boolean =>
  isInWindow(internationalEvent, today) &&
  (!isInWindow(homeEvent, today) ||
    (homeEvent.prompts.length === 0 && internationalEvent.prompts.length > 0));

export const filterEventsForMarketplace = (
  events: SeasonalEvent[],
  marketplace: FirestoreOrganisationMarketplace | null | undefined,
  today: string,
): SeasonalEvent[] => {
  const homeRegion = getSeasonalEventRegion(marketplace);
  const visible = events.filter(
    (event) =>
      event.region === homeRegion ||
      event.region === SeasonalEventRegion.INTERNATIONAL,
  );
  const homeEvents = visible.filter((event) => event.region === homeRegion);
  const internationalEvents = visible.filter(
    (event) => event.region === SeasonalEventRegion.INTERNATIONAL,
  );

  const hiddenIds = new Set<string>();
  for (const homeEvent of homeEvents) {
    for (const internationalEvent of internationalEvents) {
      if (isSameEvent(homeEvent, internationalEvent)) {
        hiddenIds.add(
          homeCopyLoses(homeEvent, internationalEvent, today)
            ? homeEvent.event_id
            : internationalEvent.event_id,
        );
      }
    }
  }

  return visible.filter((event) => !hiddenIds.has(event.event_id));
};
